<template>
  <div class="ba-targeting-control">
    <div class="feature zoom-control">
      <ba-slider :min="25"
                 :max="175"
                 :default="100"
                 :buttonStep="25"
                 :value="paneScale"
                 :vertical="true"
                 :minimal="true"
                 :disabled="noData"
                 @change="onPaneScaleChange">
        <i class="tooltip">
          <span class="tooltip-text" v-html="$t('journey.control.zoom')"></span>
        </i>
      </ba-slider>
      <ba-button :icon="'icon--fit'"
                 :iconActive="'icon--fit white'"
                 :disabled="noData"
                 @click="onPaneScaleFit">
        <i class="tooltip">
          <span class="tooltip-text" v-html="$t('journey.control.fitToPage')"></span>
        </i>
      </ba-button>
    </div>

    <div class="feature">
      <div class="ba-control-wrap">
        <ba-button :icon="'icon--connections'"
                   :iconActive="'icon--connections white'"
                   :keepActive="isConnectionOptionVisible"
                   :disabled="noData || noJourney"
                   @click="toggleVisibles('ConnectionOption')">
          <i class="tooltip">
            <span class="tooltip-text" v-html="$t('journey.targeting.control.nodeConnections')"></span>
          </i>
        </ba-button>
        <transition name="show">
          <div v-if="isConnectionOptionVisible"
               class="ba-control-layer connection-option">
            <ba-control-slider :value="connectionStrength"
                               :disabled="noData"
                               @change="onConnectionOptionChange" />
            <div class="labels">
              <span v-for="name in ['max', 'min']"
                    :key="name"
                    class="ui-input-item"
                    @click="onConnectionOptionClick(name)">
                {{ $t('journey.targeting.control.nodeConnection.'+ name) }}
              </span>
            </div>
          </div>
        </transition>
      </div>

      <div class="ba-control-wrap"
           :class="{selected: !noData && numSelectedVisibleOptions > 0}">
        <ba-button :icon="'icon--check-options'"
                   :iconActive="'icon--check-options white'"
                   :keepActive="isVisibleOptionsVisible"
                   :disabled="noData"
                   @click="toggleVisibles('VisibleOptions')">
          <i class="tooltip">
            <span class="tooltip-text" v-html="$t('trend.control.visibleOptions')"></span>
          </i>
        </ba-button>
        <transition name="show">
          <div v-if="isVisibleOptionsVisible"
               class="ba-control-layer">
            <template v-for="(value, name) in visibleOptions">
              <template v-if="name !== 'nullity'">
                <span class="ui-input-item" :key="name">
                  <ui-checkbox :value="name === 'dropOff' && !isForwardDirection ? false : value"
                               :disabled="name === 'dropOff' && !isForwardDirection"
                               @input="onVisibleOptionChange(name)"
                  >
                    {{ $t(name === 'session' ? 'common.sessioni' : name === 'nullity' ? 'journey.targeting.nullityJourney' : 'trend.' + name) }}
                  </ui-checkbox>
                </span>
              </template>
              <template v-else>
                <span class="ui-input-item" :key="name" v-if="useNullity">
                  <ui-checkbox :value="name === 'dropOff' && !isForwardDirection ? false : value"
                               :disabled="name === 'dropOff' && !isForwardDirection"
                               @input="onVisibleOptionChange(name)"
                  >
                    {{ $t(name === 'session' ? 'common.sessioni' : name === 'nullity' ? 'journey.targeting.nullityJourney' : 'trend.' + name) }}
                  </ui-checkbox>
                </span>
              </template>

            </template>
          </div>
        </transition>
      </div>

      <div class="ba-control-wrap" v-if="journeyOptions.matchType !== 'exact'"
           :class="{selected: !noData && grouping}">
        <ba-button :icon="'icon--grouping'"
                   :iconActive="'icon--grouping white'"
                   :keepActive="isGroupOptionsVisible"
                   :disabled="noData || isCustomNode"
                   @click="toggleVisibles('GroupOptions')">
          <i class="tooltip">
            <span class="tooltip-text" v-html="$t('journey.control.group')"></span>
          </i>
        </ba-button>
        <transition name="show">
          <div v-if="isGroupOptionsVisible"
               class="ba-control-layer group-options">
            <span v-for="[key, name] in groupList"
                  :key="`group-${key}`"
                  class="ui-input-item"
                  :class="{spliter: key === 'none'}">
              <ui-radio :value="(
                          (!grouping && key === 'none') ||
                          (grouping && group === key)
                        )"
                        @input="onGroupChange(key)">
                {{ name }}
              </ui-radio>
            </span>
          </div>
        </transition>
      </div>
    </div>

    <div class="feature">
      <ba-button :icon="'icon--excel'"
                 :iconActive="'icon--excel white'"
                 :disabled="noData"
                 @click="onDownloadExcel">
        <i class="tooltip">
          <span class="tooltip-text" v-html="$t('journey.excel.title')" />
        </i>
      </ba-button>
    </div>

  </div>
</template>

<script>
import BaButton from '@/components/ui/ba/Button'
import BaSlider from '@/components/ui/ba/Slider'
import BaControlSlider from '@/components/trend/Slider'
import UiCheckbox from '@/components/ui/UiCheckbox'
import UiRadio from '@/components/ui/UiRadio'
import { mapState } from 'vuex'

export default {
  name: 'BaTargetingControl',
  components: {
    BaButton,
    BaSlider,
    BaControlSlider,
    UiCheckbox,
    UiRadio
  },
  props: {
    isForwardDirection: {
      type: Boolean,
      required: true
    },
    paneScale: {
      type: Number,
      required: true
    },
    grouping: {
      type: Boolean,
      required: true
    },
    group: {
      type: String,
      required: true
    },
    groupItems: {
      type: Object,
      required: true
    },
    connectionStrength: {
      type: Number,
      required: true
    },
    visibleOptions: {
      type: Object,
      required: true
    },
    numSelectedVisibleOptions: {
      type: Number,
      required: true
    },
    noData: {
      type: Boolean,
      required: true
    },
    noJourney: {
      type: Boolean,
      required: true
    },
    isCustomNode: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      isConnectionOptionVisible: false,
      isVisibleOptionsVisible: false,
      isGroupOptionsVisible: false,
      useNullity: false
    }
  },
  computed: {
    ...mapState([
      'journeyOptions',
      'journeyTargetingOptions'
    ]),
    groupList () {
      const list = Object.entries(this.groupItems)
      list.unshift(['none', this.$t('common.unapplied')])
      return list
    }
  },
  created () {
    this._visibles = ['ConnectionOption', 'VisibleOptions', 'GroupOptions']
    this._prevConnectionOptionVisible = false
  },
  mounted () {
    document.body.addEventListener('pointerdown', this.checkCloseControlLayers)
  },
  beforeDestroy () {
    document.body.removeEventListener('pointerdown', this.checkCloseControlLayers)
  },
  methods: {
    onPaneScaleChange (value) {
      this.$emit('onPaneScaleChange', value / 100)
    },
    onPaneScaleFit () {
      this.$emit('onPaneScaleFit')
    },

    onConnectionOptionClick (name) {
      this.$emit('onConnectionOptionChange', name === 'max' ? 100 : name === 'min' ? 0 : 50)
    },
    onConnectionOptionChange (value) {
      this.$emit('onConnectionOptionChange', value)
    },

    onVisibleOptionChange (name) {
      this.$emit('onVisibleOptionChange', name)
    },

    checkCloseControlLayers (e) {
      const body = document.body
      let target = e.target
      while (target !== body) {
        if (target.classList.contains('ba-control-wrap')) {
          return
        }
        target = target.parentNode
      }
      this.closeAllControlLayers()
    },
    toggleVisibles (targetControlName, onlyForHideOthers) {
      this.useNullity = window.target_nullity
      this.$emit('useNullity', this.useNullity)

      this._visibles.forEach((controlName) => {
        const propertyName = 'is' + controlName + 'Visible'
        if (controlName === targetControlName) {
          this[propertyName] = onlyForHideOthers ? this[propertyName] : !this[propertyName]
        } else {
          this[propertyName] = false
        }
      })
      if (this.isConnectionOptionVisible !== this._prevConnectionOptionVisible) {
        this._prevConnectionOptionVisible = this.isConnectionOptionVisible
        this.$emit(this.isConnectionOptionVisible ? 'onConnectionOptionOpen' : 'onConnectionOptionClose')
      }
    },
    closeAllControlLayers () {
      this.toggleVisibles('')
    },

    onChangeGrouping (value) {
      this.$emit('onChangeGrouping', value)
    },
    onGroupChange (value) {
      if (value === 'none') {
        if (this.grouping) {
          this.$emit('onChangeGrouping', false)
        }
      } else {
        this.$emit('onGroupChange', value)
      }
    },

    onDownloadExcel () {
      this.$emit('onDownloadExcel')
    }
  }
}
</script>

<style lang="scss" scoped>
.ba-targeting-control {
  height: 100%;
  padding: 34px 10px 10px;
  box-sizing: border-box;
  text-align: center;
  background-color: rgba(226, 226, 224, 0.7);
  backdrop-filter: blur(15px);
  transition: background-color 0.3s ease;

  &.disabled {
    background-color: rgba(219, 219, 216, 0.7);
  }

  .feature {
    position: relative;
    z-index: 2;
    display: inline-block;
    vertical-align: middle;
    margin: 5px 0;

    .tooltip {
      position: absolute;
      top: 50%;
      left: 38px;
      padding: 30px 20px 30px 10px;
      transform: translate3d(-40px, -50%, 0);
      opacity: 0;
      pointer-events: none;
      transition: transform 0.2s ease,
                  opacity 0.2s ease;

      &::after {
        content: '';
        background-color: #fff;
        position: absolute;
        z-index: 2;
        transform: rotate(-135deg);
        width: 8px;
        height: 8px;
        box-shadow: 1px -1px 0 0 rgba(0, 0, 0, 0.16);
        top: calc(50% - 4.5px);
        left: 6px;
      }

      .tooltip-text {
        border-radius: 6px;
        white-space: nowrap;
        box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
        background-color: #ffffff;
        padding: 8px 10px 9px;
        color: #444444 !important;
        font-size: 13px;
        line-height: 1;
      }
    }

    .ba-control-slider {
      margin: -10px -15px -10px -10px;
      background: none;
      box-shadow: none;
    }

    .ui-input-item {
      position: relative;
      height: 18px;
      white-space: nowrap;
      display: block;

      & + .ui-input-item {
        margin-top: 8px;
      }

      &.spliter + .ui-input-item {
        margin-top: 17px;
        &:before {
          position: relative;
          top: -9px;
          height: 0;
          content: '';
          margin-right: -5px;
          border-top: 1px solid #ebebeb;
          display: block;
        }
      }

      &.nullity {
        display: none;
      }
    }

    .ba-slider {
      margin: 3px 0 2px 0;
    }

    .ba-button {
      margin: 3px 0 2px 0;

      & /deep/ {
        .text {
          margin-left: 0;
        }
      }

      &.noData {
        opacity: 0.3;
      }
    }

    .ui-input-item:hover,
    .ba-slider:hover,
    .ba-button button:not(.keep-active):not([disabled]):hover {
      .tooltip {
        transform: translate3d(0, -50%, 0);
        opacity: 1;
      }
    }

    .ui_checkbox,
    .ui_radio {
      margin-left: 0;

      & /deep/ {
        .text {
          font-size: 12px;
          font-weight: 500;
          color: #555;
          line-height: 18px;
          margin-left: 24px;
        }
      }
    }

    & + .feature {
      &::before {
        content: "";
        display: block;
        width: 24px;
        height: 1px;
        margin: -2px 4px 7px;
        background-color: rgba(0, 0, 0, 0.12);
      }
    }
  }
}

.ba-control-wrap {
  position: relative;

  &.selected {
    .ba-button /deep/ button:after {
      position: absolute;
      right: 3px;
      bottom: 3px;
      width: 5px;
      height: 5px;
      content: '';
      border-radius: 100%;
      background: #64b950;
    }
  }

  .ba-control-layer {
    position: absolute;
    left: calc(100% + 5px);
    top: 3px;
    text-align: left;
    padding: 10px 15px 10px 10px;
    border-radius: 5px;
    background: #fff;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);

    &.width-controls {
      top: 4px;
    }

    &.connection-option {
      padding-bottom: 0;
      display: flex;

      .ba-control-slider {
        margin: -10px 0 0 -10px;
        padding-top: 13px;
        padding-bottom: 14px;

        & /deep/ {
          .slider {
            height: 58px;
          }
        }
      }

      .labels {
        margin-top: -2px;

        .ui-input-item {
          font-size: 12px;
          font-weight: 500;
          color: #555;
          line-height: 19px;
          cursor: pointer;

          & + .ui-input-item {
            padding-top: 0;
            margin-top: 33px;
          }
        }
      }

      &.thru-highlighting {
        .ba-control-slider {
          & /deep/ {
            .slider .dot {
              top: -3px !important;
            }
          }
        }
        .ui-input-item + .ui-input-item {
          opacity: 0.3;
          pointer-events: none;
        }
      }
    }

    &.group-options {
      .ui-input-item .ui_radio {
        max-width: 125px;
        white-space: nowrap;
        text-overflow: ellipsis;
        display: inline-block;
        overflow: hidden;
      }
    }

    &.show {
      &-enter-active,
      &-leave-active {
        transition: transform 0.2s ease,
                    opacity 0.2s ease;
      }

      &-enter,
      &-leave-to {
        opacity: 0;
        pointer-events: none;
        transform: translate3d(-40px, 0, 0);
      }
    }
  }
}
</style>
